import { triniumApi } from '@api/Trinium/slice';
import {
	Customer,
	CustomerContact,
	FetchCustomerRequest,
	PostCustomerContactResponse,
	PostCustomerContactRequest,
	PutCustomerRequest,
	transformCustomerResponse,
	transformPostCustomerContact
} from './types';
import { defaultSortComparator } from '@components/DataGrid/types';
import { getApiErrorMessage } from '@api/getApiError';
import { queueUncontrolledToast } from '@toast';

export const customersSlice = triniumApi.injectEndpoints({
	endpoints(builder) {
		return {
			fetchCustomers: builder.query<
				Customer[],
				{
					terminalNumber: string;
					startsWith: string;
					isActive?: boolean;
				}
			>({
				query: ({ terminalNumber, startsWith, isActive }) => {
					return {
						url: `/${terminalNumber}/customers`,
						params: {
							startsWith: startsWith,
							...(isActive !== undefined ? { isActive: isActive } : {})
						}
					};
				},
				transformResponse: transformCustomerResponse
			}),

			putCustomer: builder.mutation<undefined, PutCustomerRequest>({
				query: ({ terminalNumber, id, ...payload }) => {
					return {
						url: `/${terminalNumber}/customers/${id}`,
						method: 'PUT',
						body: {
							...payload
						}
					};
				},
				async onQueryStarted(_params, { queryFulfilled, dispatch }) {
					try {
						await queryFulfilled;
					} catch (response: unknown) {
						/** Type Guard to ensure we got a valid error response */
						if (response && typeof response === 'object' && 'error' in response) {
							dispatch(
								queueUncontrolledToast({
									message: getApiErrorMessage(response.error),
									type: 'message',
									severity: 'error'
								})
							);
						}
					}
				}
			}),

			fetchCustomerContacts: builder.query<CustomerContact[], FetchCustomerRequest>({
				query: ({ terminalNumber, customerId, ...params }) => ({
					url: `/${terminalNumber}/customers/${customerId}/contacts`,
					params: {
						...(params.role ? { role: params.role } : {})
					}
				}),
				transformResponse: (response: CustomerContact[]) => response.sort((a, b) => defaultSortComparator(a.name, b.name))
			}),

			addCustomerContact: builder.mutation<PostCustomerContactResponse, PostCustomerContactRequest>({
				query: ({ terminalNumber, customerId, ...payload }) => ({
					url: `/${terminalNumber}/customers/${customerId}/contacts`,
					method: 'POST',
					body: {
						...payload
					}
				}),
				transformResponse: transformPostCustomerContact,
				async onQueryStarted({ terminalNumber, customerId }, { queryFulfilled, dispatch }) {
					const result = await queryFulfilled;

					await dispatch(
						customersSlice.util.upsertQueryData(
							'fetchCustomerContacts',
							{
								terminalNumber: terminalNumber,
								customerId: customerId
							},
							[...result.data.contacts].sort((a, b) => defaultSortComparator(a.name, b.name))
						)
					);
				}
			})
		};
	}
});

export const { useLazyFetchCustomersQuery, usePutCustomerMutation, useFetchCustomerContactsQuery, useAddCustomerContactMutation } =
	customersSlice;
